import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Category } from '../../types';
import '../../styles/RecordFilters.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEraser, faCheckSquare } from '@fortawesome/free-solid-svg-icons';

interface RecordFiltersProps {
  filters: {
    startDate: Date | null;
    endDate: Date | null;
    recordType: string;
    accountType: string;
    categoryId: number[]; // Changed to array for multiple selection
    search: string;
    sortBy: string;
    sortOrder: string;
    minAmount: string;
    maxAmount: string;
    amountCurrency: 'vnd' | 'usd';
  };
  categories: Category[];
  onFilterChange: (filters: any) => void;
  onSearch: () => void;
  onResetFilters: () => void;
}

const RecordFilters: React.FC<RecordFiltersProps> = ({ filters, categories, onFilterChange, onSearch, onResetFilters }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');

  // Set current month display based on existing filters
  useEffect(() => {
    if (filters.startDate) {
      setCurrentMonth(filters.startDate);
    }
  }, [filters.startDate]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.multi-select-container')) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isDropdownOpen]);

  const formatMonthButton = (date: Date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const handlePrevMonth = () => {
    const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    const endDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 0);
    setCurrentMonth(newDate);
    onFilterChange({
      ...filters,
      startDate: newDate,
      endDate: endDate
    });
  };

  const handleNextMonth = () => {
    const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    const endDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 2, 0);
    setCurrentMonth(newDate);
    onFilterChange({
      ...filters,
      startDate: newDate,
      endDate: endDate
    });
  };

  const handleStartDateChange = (date: Date | null) => {
    onFilterChange({
      ...filters,
      startDate: date
    });
  };

  const handleEndDateChange = (date: Date | null) => {
    onFilterChange({
      ...filters,
      endDate: date
    });
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleCategoryChange = (categoryId: number) => {
    const currentIds = filters.categoryId || [];
    const newIds = currentIds.includes(categoryId)
      ? currentIds.filter(id => id !== categoryId)
      : [...currentIds, categoryId];
    

    
    onFilterChange({
      ...filters,
      categoryId: newIds
    });
  };

  const clearAllCategories = () => {
    onFilterChange({
      ...filters,
      categoryId: []
    });
  };

  const getSelectedCategoriesText = () => {
    const selectedIds = filters.categoryId || [];
    if (selectedIds.length === 0) return 'All Categories';
    if (selectedIds.length === 1) {
      const category = categories.find(c => c.id === selectedIds[0]);
      return category ? category.name : 'All Categories';
    }
    return `${selectedIds.length} selected`;
  };

  return (
    <div className="modern-filter-panel">
      {/* Date Controls */}
      <div className="date-controls">
        <button 
          type="button" 
          className="nav-btn" 
          onClick={handlePrevMonth}
          title="Previous Month"
        >
          &#8249;
        </button>
        
        <button 
          type="button" 
          className="month-btn"
          title="Current Month Filter"
        >
          {formatMonthButton(currentMonth)}
        </button>
        
        <button 
          type="button" 
          className="nav-btn" 
          onClick={handleNextMonth}
          title="Next Month"
        >
          &#8250;
        </button>

        <div className="form-group" style={{ paddingLeft: '8px' }}>
          <DatePicker
            selected={filters.startDate}
            onChange={handleStartDateChange}
            selectsStart
            startDate={filters.startDate}
            endDate={filters.endDate}
            className="date-input form-control"
            dateFormat="MM/dd/yyyy"
            placeholderText="Start Date"
          />
        </div>

        <div className="form-group">
          <DatePicker
            selected={filters.endDate}
            onChange={handleEndDateChange}
            selectsEnd
            startDate={filters.startDate}
            endDate={filters.endDate}
            minDate={filters.startDate}
            className="date-input form-control"
            dateFormat="MM/dd/yyyy"
            placeholderText="End Date"
          />
        </div>
      </div>

      {/* Filter Controls */}
      <div className="other-filters" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <div className="form-group" style={{ minWidth: 90, flex: '1 1 90px', maxWidth: 140 }}>
          <select 
            className="form-control form-select"
            value={filters.recordType}
            onChange={(e) => onFilterChange({...filters, recordType: e.target.value})}
            title="Select record type"
          >
            <option value="">All Types</option>
            <option value="income">Income</option>
            <option value="expense">Expense</option>
          </select>
        </div>
        <div className="form-group multi-select-container" style={{ minWidth: 160, flex: '2 1 200px', maxWidth: 320 }}>
          <div 
            className="form-control form-select"
            onClick={toggleDropdown}
            title="Select categories"
            style={{ minWidth: 120, width: '100%' }}
          >
            <span className="selected-text" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getSelectedCategoriesText()}</span>
          </div>
          {isDropdownOpen && (
            <div className="dropdown-panel" style={{ minWidth: 160, width: '100%', maxWidth: 320 }}>
              <div className="dropdown-header">
                <span className="dropdown-title">Select Categories</span>
                <div className="dropdown-actions">
                  <button 
                    type="button"
                    className="icon-btn"
                    title="Select All"
                    onClick={() => onFilterChange({ ...filters, categoryId: categories.map(c => c.id) })}
                  >
                    <FontAwesomeIcon icon={faCheckSquare} />
                  </button>
                  <button 
                    type="button"
                    className="icon-btn"
                    title="Clear All"
                    onClick={clearAllCategories}
                  >
                    <FontAwesomeIcon icon={faEraser} />
                  </button>
                  <button 
                    type="button"
                    className="close-dropdown-btn"
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    ✕
                  </button>
                </div>
              </div>
              <div className="category-search-container">
                <input
                  type="text"
                  className="form-control search-input category-search-input"
                  placeholder="Search category..."
                  value={categorySearch}
                  onChange={e => setCategorySearch(e.target.value)}
                />
              </div>
              <div className="dropdown-content">
                {categories.filter(c => c.name.toLowerCase().includes(categorySearch.toLowerCase())).length > 0 ? (
                  categories
                    .filter(c => c.name.toLowerCase().includes(categorySearch.toLowerCase()))
                    .map((category) => (
                      <div key={category.id} className="dropdown-option">
                        <span
                          className={`category-dot ${category.type === 'expense' ? 'category-dot-expense' : 'category-dot-income'}`}
                        ></span>
                        <input
                          type="checkbox"
                          className="option-checkbox"
                          checked={filters.categoryId?.includes(category.id) || false}
                          onChange={() => handleCategoryChange(category.id)}
                          title={`Select ${category.name}`}
                        />
                        <span className="option-label">{category.name}</span>
                      </div>
                    ))
                ) : (
                  <div className="no-options">No categories available</div>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="form-group form-group-min-width-120">
          <input
            type="text"
            className="form-control search-input search-input-ellipsis"
            placeholder="Search descriptions..."
            value={filters.search}
            onChange={(e) => onFilterChange({...filters, search: e.target.value})}
          />
        </div>
        <div className="form-group form-group-min-width-60">
          <input
            type="text"
            className="form-control amount-input"
            placeholder="Min"
            value={filters.minAmount}
            onChange={(e) => onFilterChange({...filters, minAmount: e.target.value})}
          />
        </div>
        <div className="form-group form-group-min-width-60">
          <input
            type="text"
            className="form-control amount-input"
            placeholder="Max"
            value={filters.maxAmount}
            onChange={(e) => onFilterChange({...filters, maxAmount: e.target.value})}
          />
        </div>
        <div className="form-group form-group-min-width-50">
          <select 
            className="form-control form-select currency-select"
            value={filters.amountCurrency}
            onChange={(e) => onFilterChange({...filters, amountCurrency: e.target.value as 'vnd' | 'usd'})}
            title="Select currency"
          >
            <option value="vnd">VND</option>
            <option value="usd">USD</option>
          </select>
        </div>
        <div className="form-group form-group-min-width-32">
          <button 
            type="button" 
            className="search-btn"
            onClick={onSearch}
          >
            🔍
          </button>
        </div>
        <div className="form-group form-group-min-width-32">
          <button 
            type="button" 
            className="reset-btn"
            onClick={onResetFilters}
          >
            ↻
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecordFilters;
